import type {
  BlockToolQuickAccessStoryblok,
  BlockToolServiceProvidersStoryblok,
} from '~/types/storyblok'

interface ToolFilters {
  states?: string[]
  serviceType?: string[]
  audiences?: string[]
  tools?: string[]
}

export type ToolStoryblok =
  | BlockToolServiceProvidersStoryblok
  | BlockToolQuickAccessStoryblok

export default async function useTool({
  blok,
  filters,
  selectedAudiences = ref([]),
}: {
  blok: Ref<ToolStoryblok>
  filters: Ref<ToolFilters>
  selectedAudiences?: Ref<string[]>
}) {
  const { fetchAudiences } = useStoryblokDataSources()
  const { data: datasourceAudiences } = await fetchAudiences()
  const isBusy = ref(false)

  const currentAudience = computed(() => {
    // If filter selection set, use the selected Audience
    if (filters.value?.audiences?.length) {
      return filterAudience(filters.value.audiences[0])
    }

    // When no filter selection and no placeholder supplied
    // fallback by using a randomised Audience as backup
    if (selectedAudiences.value?.includes('all')) {
      const max = datasourceAudiences.value.length
      const rand = Math.floor(Math.random() * max)

      return [datasourceAudiences.value[rand]]
    } else {
      const max =
        selectedAudiences.value?.length ||
        datasourceAudiences.value?.length ||
        1
      const rand = Math.floor(Math.random() * max)

      return selectedAudiences.value?.length
        ? filterAudience(selectedAudiences.value?.[rand])
        : [datasourceAudiences.value[rand]]
    }
  })

  const audienceImage = computed(() => {
    // if placeholder exists and no filter selection set
    // show placeholder image
    if (
      blok.value?.placeholder?.filename &&
      !filters.value?.audiences?.length
    ) {
      return blok.value.placeholder
    }

    // else use image from audience
    return {
      alt:
        currentAudience.value[0]?.singularTerm ||
        currentAudience.value[0]?.name ||
        '',
      filename: currentAudience.value[0]?.media,
    }
  })

  function filterAudience(audienceValue: string) {
    return datasourceAudiences.value.filter((item) => {
      return item?.value === audienceValue
    })
  }

  return {
    currentAudience,
    audienceImage,
    filterAudience,
    isBusy,
  }
}
